import type { HttpClient } from 'effect/unstable/http';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import type { Context, ManagedRuntime } from 'effect';
import { createEffectQueryFromManagedRuntime } from 'effect-query';
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useMemo,
} from 'react';

// Shared React-context plumbing so cross-app TanStack-Query hooks
// (`useUnarchiveAudioMutation`, the optimistic list-entity lifecycle, etc.)
// can find "the runtime for this app" without per-callsite wiring. Each app
// builds its own `ManagedRuntime` (start has `clientRuntime`, browser-ext
// supplies its own, mobile its own) and mounts `<AppRuntimeProvider runtime>`
// once at the QueryClient root. Shared hooks read it via `useAppRuntime`
// (for `Effect` runPromise) or `useEq` (for `eq.queryOptions` /
// `eq.mutationOptions`, which return EffectQueryFailure on rejection).
//
// See docs/projects/complete/web/shared-tanstack-query.md.

type ApiClientCtx = Context.Service.Identifier<typeof ApiClient>;

/** Everything a shared program may ask of the app runtime. Named so callers
 * that hand an Effect to `runtime.runFork` can state the requirement. */
export type AppServices = ApiClientCtx | HttpClient.HttpClient;

// Minimum the shared layer needs. Each app's actual runtime may provide more
// (Logger, Analytics, etc.) — covariant in R, so wider runtimes assign
// to this type. HttpClient is here for the doc-pack ranged reads (and any
// other shared program that talks to plain HTTP, not the typed ApiClient) —
// every app runtime supplies FetchHttpClient.layer.
export type AppRuntime = ManagedRuntime.ManagedRuntime<AppServices, never>;

export type Eq = ReturnType<typeof createEffectQueryFromManagedRuntime>;
type AppRuntimeContextValue = { runtime: AppRuntime; eq: Eq };

/** The same `eq` the provider hands to hooks, for code that runs outside React
 * (launch preflights, imperative `queryClient.fetchQuery`) and must share a
 * query's key, span and error envelope with the hooks reading it. Stateless —
 * a closure over the runtime — so a second instance is free. */
export const makeEq = (runtime: AppRuntime) =>
  createEffectQueryFromManagedRuntime(runtime);

const AppRuntimeContext = createContext<AppRuntimeContextValue | null>(null);

export function AppRuntimeProvider({
  runtime,
  children,
}: {
  runtime: AppRuntime;
  children: ReactNode;
}) {
  // Derive `eq` from `runtime` and memoize — the same runtime should produce
  // a stable eq across renders so dependent hooks don't churn options.
  const value = useMemo<AppRuntimeContextValue>(
    () => ({ runtime, eq: makeEq(runtime) }),
    [runtime],
  );
  return createElement(AppRuntimeContext.Provider, { value }, children);
}

function useAppRuntimeContext(): AppRuntimeContextValue {
  const v = useContext(AppRuntimeContext);
  if (!v) {
    throw new Error(
      'AppRuntimeProvider missing from React tree — wrap your QueryClientProvider in <AppRuntimeProvider runtime={...}>.',
    );
  }
  return v;
}

export const useAppRuntime = (): AppRuntime => useAppRuntimeContext().runtime;
export const useEq = (): Eq => useAppRuntimeContext().eq;
